import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import LeadsImportPage from "@/pages/LeadsImportPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import CreateLeadPage from "@/pages/CreateLeadPage";
import EmployeesPage from "@/pages/EmployeesPage";
import CalendarPage from "@/pages/CalendarPage";
import InquiryFormPage from "@/pages/InquiryFormPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ContractTemplatesPage from "@/pages/ContractTemplatesPage";
import SignContractPage from "@/pages/SignContractPage";
import SignExternalDocumentPage from "@/pages/SignExternalDocumentPage";
import PublicWaiverPage from "@/pages/PublicWaiverPage";
import SignedWaiversPage from "@/pages/SignedWaiversPage";
import WaiverQRPosterPage from "@/pages/WaiverQRPosterPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import ReportsPage from "@/pages/ReportsPage";
import AuditLogPage from "@/pages/AuditLogPage";
import ForgotPasswordPage, { ResetPasswordPage } from "@/pages/ForgotPasswordPage";
import VenuesPage from "@/pages/VenuesPage";
import MassBlastPage from "@/pages/MassBlastPage";
import GoogleCalendarPage from "@/pages/GoogleCalendarPage";
import AdminToolsPage from "@/pages/AdminToolsPage";
import EmailHubPage from "@/pages/email/EmailHubPage";
import ContactsPage from "@/pages/email/ContactsPage";
import ListsPage from "@/pages/email/ListsPage";
import TemplatesPage from "@/pages/email/TemplatesPage";
import CampaignsPage from "@/pages/email/CampaignsPage";
import CampaignBuilderPage from "@/pages/email/CampaignBuilderPage";
import CampaignDetailPage from "@/pages/email/CampaignDetailPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import "@/App.css";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/inquire" element={<InquiryFormPage />} />
      <Route path="/sign-contract" element={<SignContractPage />} />
      <Route path="/sign-document/:token" element={<SignExternalDocumentPage />} />
      <Route path="/waiver" element={<PublicWaiverPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/leads/import" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><LeadsImportPage /></ProtectedRoute>} />
      <Route path="/leads/new" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><CreateLeadPage /></ProtectedRoute>} />
      <Route path="/leads/:id" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><EmployeesPage /></ProtectedRoute>} />
      <Route path="/contract-templates" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><ContractTemplatesPage /></ProtectedRoute>} />
      <Route path="/venues" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><VenuesPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute roles={["super_admin"]}><AuditLogPage /></ProtectedRoute>} />
      <Route path="/blast" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><MassBlastPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/settings/calendar" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><GoogleCalendarPage /></ProtectedRoute>} />
      <Route path="/admin/testing" element={<ProtectedRoute roles={["super_admin"]}><AdminToolsPage /></ProtectedRoute>} />
      <Route path="/email" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><EmailHubPage /></ProtectedRoute>} />
      <Route path="/email/contacts" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><ContactsPage /></ProtectedRoute>} />
      <Route path="/email/lists" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><ListsPage /></ProtectedRoute>} />
      <Route path="/email/templates" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><TemplatesPage /></ProtectedRoute>} />
      <Route path="/email/campaigns" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><CampaignsPage /></ProtectedRoute>} />
      <Route path="/email/campaigns/new" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><CampaignBuilderPage /></ProtectedRoute>} />
      <Route path="/email/campaigns/:id" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><CampaignDetailPage /></ProtectedRoute>} />
      <Route path="/email/campaigns/:id/edit" element={<ProtectedRoute roles={["super_admin", "company_admin"]}><CampaignBuilderPage /></ProtectedRoute>} />
      <Route path="/signed-waivers" element={<ProtectedRoute><SignedWaiversPage /></ProtectedRoute>} />
      <Route path="/signed-waivers/qr" element={<ProtectedRoute><WaiverQRPosterPage /></ProtectedRoute>} />
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      <Route path="/" element={<InquiryFormPage />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

